import { can } from "../features/auth/permissions";

// Central navigation config.
// Each section becomes a labeled group in the sidebar.
// Items are flat (no nesting) — add icon + to + `resource`: раздел матрицы прав
// (`access/registry.py`), пункт виден при праве «Просмотр». Без `resource` — виден всем.
export const NAV_SECTIONS = [
  {
    key: "main",
    label: "Главная",
    items: [
      {
        key: "home",
        label: "Home",
        to: "/",
        icon: "home",
        resource: "home",
      },
    ],
  },
  {
    key: "sales",
    label: "Продажи",
    items: [
      {
        key: "dashboard_sales",
        label: "Дашборд продаж",
        to: "/sales",
        icon: "chartLine",
        resource: "sales_dashboard",
      },
      {
        key: "dashboard_online_sales",
        label: "Онлайн продажи",
        to: "/sales/online",
        icon: "cart",
        resource: "online_sales",
      },
      {
        key: "online_seller",
        label: "Розничная касса",
        to: "/sales/online/seller",
        icon: "card",
        resource: "online_seller",
      },
      {
        key: "wholesale_customers",
        label: "Опт — клиенты",
        to: "/wholesale",
        icon: "users",
        resource: "wholesale_customer",
      },
      {
        key: "wholesale_sales",
        label: "Опт — продажи",
        to: "/wholesale/sales",
        icon: "clipboard",
        resource: "wholesale_sale",
      },
    ],
  },
  {
    key: "stores",
    label: "Магазины",
    items: [
      {
        key: "store_shifts",
        label: "Смены по магазинам",
        to: "/management/store-shifts",
        icon: "clock",
        resource: "store_shifts",
      },
    ],
  },
  {
    key: "warehouse",
    label: "Склад",
    items: [
      {
        key: "warehouse_dashboard",
        label: "Дашборд",
        to: "/warehouse",
        icon: "chartBar",
        resource: "warehouse_dashboard",
      },
      {
        key: "warehouse_products",
        label: "Товары",
        to: "/warehouse/products",
        icon: "box",
        resource: "product",
      },
      {
        key: "warehouse_stock_overview",
        label: "Остатки товара",
        to: "/warehouse/stock-overview",
        icon: "layers",
        resource: "stock",
      },
      {
        key: "warehouse_returns",
        label: "Возвраты",
        to: "/warehouse/returns",
        icon: "undo",
        resource: "returns",
      },
      {
        key: "warehouse_stores",
        label: "Склады",
        to: "/warehouse/stores",
        icon: "warehouse",
        resource: "store",
      },
      {
        key: "warehouse_categories",
        label: "Категории",
        to: "/warehouse/categories",
        icon: "tag",
        resource: "category",
      },
      {
        key: "warehouse_revision",
        label: "Ревизия",
        to: "/warehouse/revision",
        icon: "checklist",
        resource: "inventory_revision",
      },
      {
        key: "warehouse_revision_reports",
        label: "Отчёт по ревизиям",
        to: "/warehouse/revision/reports",
        icon: "report",
        resource: "revision_report",
      },
      {
        key: "warehouse_transfers",
        label: "Перемещения",
        to: "/warehouse/transfers",
        icon: "transfer",
        resource: "stock_transfer",
      },
      {
        key: "warehouse_purchase_requests",
        label: "Запросы на закуп",
        to: "/warehouse/purchase-requests",
        icon: "purchase",
        resource: "purchase_request",
      },
    ],
  },
  {
    key: "accounting",
    label: "Бухгалтерия",
    items: [
      {
        key: "accounting_overview",
        label: "Обзор",
        to: "/accounting",
        icon: "money",
        resource: "accounting",
      },
      {
        key: "accounting_pnl",
        label: "Прибыльность (P&L)",
        to: "/accounting/pnl",
        icon: "trendUp",
        resource: "report_pnl",
      },
      {
        key: "accounting_wholesale_sales",
        label: "Опт продажи",
        to: "/accounting/wholesale-sales",
        icon: "boxOut",
        resource: "report_wholesale",
      },
      {
        key: "accounting_online_sales",
        label: "Онлайн продажи (отчёт)",
        to: "/accounting/online-sales",
        icon: "storefront",
        resource: "report_online",
      },
    ],
  },
  {
    key: "management",
    label: "Управление",
    items: [
      {
        key: "roles",
        label: "Роли и права",
        to: "/management/roles",
        icon: "shield",
        resource: "role",
      },
      {
        key: "users",
        label: "Пользователи",
        to: "/management/users",
        icon: "userKey",
        resource: "user",
      },
      {
        key: "settings",
        label: "Настройки",
        to: "/settings",
        icon: "settings",
      },
    ],
  },
];

export function canSeeNavItem(user, item) {
  return item.resource ? can(user, item.resource) : true;
}

export function getVisibleNavSections(user) {
  return NAV_SECTIONS.map((section) => ({
    ...section,
    items: (section.items || []).filter((item) => canSeeNavItem(user, item)),
  })).filter((s) => s.items.length > 0);
}

/**
 * «Секция → Пункт» для адреса — чтобы на экране «Нет доступа» назвать раздел по-человечески.
 * Берётся самый длинный подходящий префикс: `/warehouse/revision/reports` → «Отчёт по ревизиям»,
 * а не «Ревизия». Адреса вне меню — `null`.
 */
export function findNavLabel(pathname) {
  let best = null;
  let bestLen = -1;
  for (const section of NAV_SECTIONS) {
    for (const item of section.items || []) {
      const match = item.to === "/" ? pathname === "/" : pathname === item.to || pathname.startsWith(`${item.to}/`);
      if (match && item.to.length > bestLen) {
        best = `${section.label} → ${item.label}`;
        bestLen = item.to.length;
      }
    }
  }
  return best;
}

/**
 * Стартовая страница пользователя: «/» при праве на главную, иначе первый доступный пункт
 * меню. Куда отправлять после входа и откуда возвращать из закрытого «/». Раньше правило
 * было зашито по группам (ревизор → ревизия, склад → онлайн-продажи) и не знало о матрице.
 */
export function getLandingRoute(user) {
  if (can(user, "home")) return "/";
  for (const section of NAV_SECTIONS) {
    for (const item of section.items || []) {
      if (item.resource && canSeeNavItem(user, item)) return item.to;
    }
  }
  return "/settings";
}
